import React, { ReactNode, useEffect, useRef } from 'react';
/* eslint-disable jsx-a11y/no-autofocus -- autoFocus is forwarded to react-focus-lock, not a DOM element. */
import { transparentize } from '@osmosis/shared/styles/colorModifiers';
import { faTimes } from '@fortawesome/pro-regular-svg-icons/faTimes';
import { Box } from '@osmosis/tailwind-ui/components/Box';
import { Flex } from '@osmosis/tailwind-ui/components/Box';
import { H4 } from '@osmosis/tailwind-ui/components/Text';
import { Icon } from '@osmosis/tailwind-ui/components/Icon';
import { IconButton } from '@osmosis/tailwind-ui/components/IconButton';
import { ZIndex } from '@osmosis/tailwind-ui/helpers/zIndex';
import { text4, text5 } from '@osmosis/tailwind-ui/styles/text';
import { AnimatePresence } from 'motion/react';
import MotionDiv from '@osmosis/nextjs-components/MotionDiv';

import useKeypress from '../hooks/useKeypress';
import useClientSide from '../hooks/useClientSide';
import useFixScroll from '../hooks/useFixScroll';
import FocusLock from 'react-focus-lock';
import { createPortal } from 'react-dom';
import { ModalHookProps } from '../hooks/useModal';

export type ModalProps = {
  hide: () => void;
  data?: ModalHookProps['data'];
  isVisible: boolean;
  disableHide?: boolean;
  disableEscapeClose?: boolean;
  children?: ReactNode;
  hasHeader?: boolean;
  headerBackground?: string;
  headerContent?: ReactNode;
  headerColor?: string;
  darkMode?: boolean;
  onEnter?: () => void;
  zIndex?: number;
  hideIconClasses?: string;
  headerClasses?: string;
  overlayOpacity?: number;
  isReturnFocus?: boolean;
  autoFocus?: boolean;
  customReturnFocusTargetTestId?: string | null; // If the focus lock isn't working as expected, you can pass a test id to focus on
  className?: string;
  _className?: string;
  ariaModal?: boolean;
  'aria-modal'?: React.AriaAttributes['aria-modal'];
  'aria-label'?: React.AriaAttributes['aria-label'];
  role?: string;
  disableOverflowAuto?: boolean;
  modalContentCss?: React.CSSProperties;
};

export const closeButtonTestId = 'close-button';
const ariaLabel = 'Close';

/**
 * Custom modal component.
 */
const Modal = (props: ModalProps): JSX.Element => {
  const {
    children,
    isVisible,
    hasHeader = Boolean(props.headerContent),
    headerBackground = 'bg-white',
    headerContent,
    headerColor,
    hide,
    disableHide = false,
    disableEscapeClose = false,
    onEnter,
    className = '',
    _className,
    zIndex = ZIndex.MODAL,
    hideIconClasses = '',
    headerClasses = '',
    overlayOpacity = 0.5,
    isReturnFocus = true,
    autoFocus = false,
    customReturnFocusTargetTestId,
    ariaModal,
    role,
    modalContentCss = {},
  } = props;
  const ariaModalValue = props['aria-modal'] ?? ariaModal ?? true;

  // Keep track of whether we've fixed from this instance yet
  const isFixedRef = useRef(isVisible);

  const isClient = useClientSide();
  const handleFixScroll = useFixScroll();

  // Call enter on enter
  useKeypress('enter', onEnter);
  // Close on escape
  useKeypress('escape', () => {
    if (!disableEscapeClose) {
      hide();
    }
  });

  const handleBackdropKeyDown = (
    event: React.KeyboardEvent<HTMLDivElement>,
  ) => {
    if (disableHide) return;

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      hide();
    }
  };

  const handleModalContentClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (disableHide || event.target !== event.currentTarget) return;

    hide();
  };

  const handleModalContentKeyDown = (
    event: React.KeyboardEvent<HTMLDivElement>,
  ) => {
    if (event.target !== event.currentTarget) return;

    handleBackdropKeyDown(event);
  };

  useEffect(() => {
    // When the modal is shown, if we haven't fixed yet, fix the scroll
    if (isVisible && !isFixedRef.current) {
      handleFixScroll(true);
      isFixedRef.current = true;
      // When the modal is hidden, if we have fixed, unfix the scroll
    } else if (!isVisible && isFixedRef.current) {
      handleFixScroll(false);
      isFixedRef.current = false;
    }

    return () => {
      if (isVisible && isFixedRef.current) {
        handleFixScroll(false);
      }
    };
  }, [handleFixScroll, isVisible]);

  const modalContent = () => (
    <Box
      className={`fixed left-0 top-0 h-full w-full ${
        isVisible ? 'pointer-events-auto' : 'pointer-events-none'
      }`}
      style={{ zIndex }}
    >
      <AnimatePresence>
        {isVisible && (
          <MotionDiv
            style={{
              position: 'absolute',
              width: '100%',
              height: '100%',
              left: 0,
              top: 0,
              zIndex: ZIndex.MODAL_BACKGROUND,
              background: transparentize(overlayOpacity, '#000'),
            }}
            onClick={disableHide ? undefined : hide}
            onKeyDown={handleBackdropKeyDown}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            data-testid="modal-background"
            key="background"
          />
        )}

        {isVisible && (
          <MotionDiv
            data-testid="motion div"
            style={{
              position: 'absolute',
              width: '100%',
              height: '100%',
              left: 0,
              top: 0,
              zIndex: ZIndex.MODAL_BACKGROUND,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              ...modalContentCss,
            }}
            initial={{ opacity: 0, y: 40, scale: 0.75 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{
              opacity: 0,
              y: 20,
              transition: { ease: 'easeOut' },
            }}
            key="content"
            onClick={handleModalContentClick}
            onKeyDown={handleModalContentKeyDown}
          >
            <Box
              data-testid="content-container"
              className={`${
                !props.disableOverflowAuto ? 'overflow-auto' : ''
              } max-h-[70vh] min-w-[350px] max-w-full rounded ${_className}`}
              aria-modal={ariaModalValue}
              aria-label={props['aria-label']}
              style={{ backgroundColor: 'white' }}
              role={role}
            >
              {hasHeader && (
                <Flex
                  className={`flex items-center justify-between rounded-t-[5px] py-2 pl-3 pr-2 ${headerBackground}`}
                >
                  <H4
                    className={`font-normal ${
                      headerColor || text5
                    } ${headerClasses}`}
                  >
                    {headerContent}
                  </H4>
                  {!disableHide && (
                    <IconButton
                      type="button"
                      variant="transparent"
                      onClick={hide}
                      data-testid={closeButtonTestId}
                      aria-label={ariaLabel}
                      className={`${text4} ${hideIconClasses}`}
                      icon={<Icon icon={faTimes} width="10px" />}
                    />
                  )}
                </Flex>
              )}

              <Box className={`rounded-bl-[5px] rounded-br-[5px] ${className}`}>
                {children}
              </Box>
            </Box>
          </MotionDiv>
        )}
      </AnimatePresence>
    </Box>
  );

  return (
    <>
      {isClient &&
        isVisible &&
        createPortal(
          <FocusLock
            disabled={!isVisible}
            returnFocus={isReturnFocus}
            autoFocus={autoFocus}
            onDeactivation={() => {
              // When using the disabled prop, it's more consistent to setTimeout to ensure the code is ran
              setTimeout(() => {
                if (customReturnFocusTargetTestId)
                  (
                    document.querySelector(
                      `[data-testid="${customReturnFocusTargetTestId}"]`,
                    ) as HTMLElement
                  )?.focus();
              }, 0);
            }}
          >
            {modalContent()}
          </FocusLock>,
          document?.body,
        )}
    </>
  );
};

export default Modal;
